import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  // Check if this is an Emotion/styled component error
  const isEmotionError =
    error.message?.includes('is not a function') &&
    (error.stack?.includes('emotion') || error.stack?.includes('styled'));

  const isH1Error = error.message?.includes('H1 is not a function');

  if (isEmotionError || isH1Error) {
    console.error('Emotion/Styled Components Error:', {
      message: error.message,
      stack: error.stack,
      type: 'emotion_bundling_error',
    });

    // Try to reload the page in production to refetch chunks
    if (process.env.NODE_ENV === 'production') {
      setTimeout(() => {
        window.location.reload();
      }, 1000);

      return (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <h2>Loading resources...</h2>
          <p>The page will reload automatically to fix a loading issue.</p>
          <p>
            <small>If this continues, please clear your browser cache.</small>
          </p>
        </div>
      );
    }
  }

  return (
    <div
      role='alert'
      style={{
        padding: '40px',
        textAlign: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ maxWidth: '600px' }}>
        <h1 style={{ color: '#d32f2f', marginBottom: '16px' }}>Application Error</h1>
        <p style={{ color: '#666', marginBottom: '24px', fontSize: '18px' }}>
          A critical error occurred that prevented the application from loading properly. This may be due to a system
          issue or browser compatibility problem.
        </p>
        <details
          style={{
            whiteSpace: 'pre-wrap',
            marginTop: '20px',
            textAlign: 'left',
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #ddd',
          }}
        >
          <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Technical Details</summary>
          <strong>Error:</strong> {error.message}
          <br />
          <br />
          <strong>Stack Trace:</strong>
          <br />
          {error.stack}
        </details>
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={resetErrorBoundary}
            style={{
              marginRight: '12px',
              padding: '12px 24px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500',
            }}
          >
            Reload Page
          </button>
        </div>
        <p
          style={{
            color: '#999',
            fontSize: '14px',
            marginTop: '24px',
          }}
        >
          If this problem persists, please contact support or try using a different browser.
        </p>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

export default function ErrorBoundary({ children }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) => {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Log to external service if available
        if (typeof window !== 'undefined' && (window as any).Sentry) {
          (window as any).Sentry.captureException(error, {
            contexts: {
              errorBoundary: {
                componentStack: errorInfo.componentStack,
              },
            },
          });
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}